import ChevronDown16 from '@carbon/icons-react/es/chevron--down/16';
import ChevronRight16 from '@carbon/icons-react/es/chevron--right/16';
import Close16 from '@carbon/icons-react/es/close/16';
import Search16 from '@carbon/icons-react/es/search/16';
import { Tooltip } from 'antd';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Checkbox } from 'components/atoms/checkbox';
import { Panel } from 'components/atoms/collapse';
import { Input } from 'components/atoms/input';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Popover } from 'components/atoms/popover';
import { FilterableField } from 'components/organisms/tasks/fields';
import { useTranslation } from 'hooks/use-translation';
import { getHeaderName } from 'routes/projects/list-v2/grid/get-header-name';
import {
  StyledMenu,
  StyledCollapse,
  SearchInput,
  StyledLinkButton,
  StyledButton,
  ButtonWrapper,
  ListContainer,
  MenuItem,
  CollapseHeader,
  Header,
  Trigger,
} from './style';
import { getFilterdFields, FilterFieldOptions } from './utils';

const DropdownMenu = ({
  fieldIds,
  fields,
  handleFieldSelectChange,
  handleClickDone,
}: {
  fieldIds: number[];
  fields: FilterableField[];
  handleFieldSelectChange: (fieldIds: number[]) => void;
  handleClickDone: () => void;
}) => {
  const [searchText, setSearchText] = useState<string>('');
  const defaultFieldIds = [-3, -14, -100004, -12];
  const handleFieldSelect = (event) => {
    if (!event.target.checked) {
      const currentField = fields.filter(
        (field) => field.fieldName === event.target.name,
      );
      const newfieldIds = fieldIds.filter(
        (field) => field !== currentField[0].fieldId,
      );

      handleFieldSelectChange(newfieldIds);
    } else {
      const field = fields.filter(
        (projectfield) => projectfield.fieldName === event.target.name,
      );
      const newFieldIds = [...fieldIds, field[0].fieldId];

      handleFieldSelectChange(newFieldIds);
    }
  };

  const selectedFiltered = getFilterdFields(
    fields,
    searchText,
    fieldIds,
    FilterFieldOptions.SELECTED,
  );

  const unSelectedFiltered = getFilterdFields(
    fields,
    searchText,
    fieldIds,
    FilterFieldOptions.UNSELECTED,
  );
  const handleReset = (event) => {
    event.stopPropagation();
    handleFieldSelectChange(defaultFieldIds);
  };
  const selectedFieldsLabel = useTranslation(
    'project_summary.selected_fields.collapse.header',
    {
      count: selectedFiltered.length,
    },
  );
  const otherFieldsLabel = useTranslation(
    'project_summary.other_fields.collapse.header',
    {
      count: unSelectedFiltered.length,
    },
  );
  const handleClearSearchText = () => setSearchText('');

  return (
    <StyledMenu selectable={false}>
      <Header>
        <FormattedMessage id="project_fields" />
      </Header>
      <SearchInput>
        <Input
          placeholder={useTranslation('search')}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        {searchText ? (
          <Close16 onClick={handleClearSearchText} />
        ) : (
          <Search16 />
        )}
      </SearchInput>
      <ListContainer itemLayout="vertical" split={false}>
        <StyledCollapse
          key="selected"
          defaultActiveKey="selected"
          expandIconPosition="left"
          ghost
          expandIcon={({ isActive }) => {
            return isActive ? <ChevronDown16 /> : <ChevronRight16 />;
          }}
        >
          <Panel
            key="selected"
            showArrow
            header={
              <CollapseHeader>
                <div>{selectedFieldsLabel}</div>
                <StyledLinkButton type="link" onClick={handleReset}>
                  <FormattedMessage id="reset" />
                </StyledLinkButton>
              </CollapseHeader>
            }
          >
            {selectedFiltered.map((field) => {
              const label = getHeaderName(field);

              return (
                <MenuItem key={field.fieldId}>
                  {label && label.length > 25 ? (
                    <Tooltip title={label} placement="topRight">
                      <Checkbox
                        name={field.fieldName}
                        checked={!!fieldIds.find((id) => id === field.fieldId)}
                        onChange={(event) => handleFieldSelect(event)}
                      >
                        {label}
                      </Checkbox>
                    </Tooltip>
                  ) : (
                    <Checkbox
                      name={field.fieldName}
                      checked={!!fieldIds.find((id) => id === field.fieldId)}
                      onChange={(event) => handleFieldSelect(event)}
                    >
                      {label}
                    </Checkbox>
                  )}
                </MenuItem>
              );
            })}
          </Panel>
        </StyledCollapse>

        <StyledCollapse
          key="unselected"
          defaultActiveKey="unselected"
          expandIconPosition="left"
          ghost
          expandIcon={({ isActive }) => {
            return isActive ? <ChevronDown16 /> : <ChevronRight16 />;
          }}
        >
          <Panel
            key="unselected"
            showArrow
            header={<CollapseHeader>{otherFieldsLabel}</CollapseHeader>}
          >
            {unSelectedFiltered.map((field) => {
              const label = getHeaderName(field);

              return (
                <MenuItem key={field.fieldId}>
                  {label && label.length > 25 ? (
                    <Tooltip title={label} placement="topRight">
                      <Checkbox
                        name={field.fieldName}
                        checked={!!fieldIds.find((id) => id === field.fieldId)}
                        onChange={(event) => handleFieldSelect(event)}
                      >
                        {label}
                      </Checkbox>
                    </Tooltip>
                  ) : (
                    <Checkbox
                      name={field.fieldName}
                      checked={!!fieldIds.find((id) => id === field.fieldId)}
                      onChange={(event) => handleFieldSelect(event)}
                    >
                      {label}
                    </Checkbox>
                  )}
                </MenuItem>
              );
            })}
          </Panel>
        </StyledCollapse>
      </ListContainer>
      <ButtonWrapper>
        <StyledButton onClick={handleClickDone}>
          <FormattedMessage id="done" />
        </StyledButton>
      </ButtonWrapper>
    </StyledMenu>
  );
};

export const ProjectFieldsFilterPopover = ({
  fieldIds,
  fields,
  handleFieldSelectChange,
}: {
  fieldIds: number[];
  fields: FilterableField[];
  handleFieldSelectChange: (fieldIds: number[]) => void;
}) => {
  const [visible, setVisible] = useState<boolean>(false);
  const handleClickDone = () => {
    setVisible(false);
  };

  return (
    <Popover
      content={
        <OutsideClickHandler onOutsideClick={() => setVisible(false)}>
          <DropdownMenu
            fieldIds={fieldIds}
            fields={fields}
            handleFieldSelectChange={handleFieldSelectChange}
            handleClickDone={handleClickDone}
          />
        </OutsideClickHandler>
      }
      trigger={['click']}
      visible={visible}
    >
      <Trigger onClick={() => setVisible(true)}>
        <FormattedMessage id="status-updates.project_summary.choose_project_fields.popover.trigger" />
      </Trigger>
    </Popover>
  );
};
